import type { CSSProperties } from 'vue'
import type { SwatchColor } from '~/types/swatch'

export function useSwatch() {
  /**
   * Generates CSS styles for color swatches based on the provided swatch color string.
   * @param swatchColor - The swatch color string to parse, e.g. "Musta/valkoinen|#000000|#FFFFFF"
   * @returns CSS styles for color swatches, including background color and borders for white swatches.
   */
  const getSwatchStyle = (swatchColor: SwatchColor, options = { border: true }): CSSProperties => {
    const colors = swatchColor.split('|').slice(1).filter(Boolean)

    if (!colors.length) {
      return {
        backgroundImage: 'radial-gradient(circle, rgba(245,245,245,1) 0%, rgba(245,245,245,1) 31%, rgba(255,255,255,1) 31%, rgba(255,255,255,1) 39%, rgba(245,245,245,1) 39%, rgba(245,245,245,1) 100%)'
      }
    }

    const bgImgs: string[] = []

    if (colors.length === 2) {
      bgImgs.push(`linear-gradient(90deg, ${colors[0]} 50%, ${colors[1]} 50%, ${colors[1]} 100%)`)
    }
    else if (colors.length === 3) {
      bgImgs.push(`linear-gradient(90deg, ${colors[0]} 33%, ${colors[1]} 33%, ${colors[1]} 66%, ${colors[2]} 66%, ${colors[2]} 100%)`)
    }

    const styles: CSSProperties = {}

    if (colors[0]) {
      styles.backgroundColor = colors[0]
      styles.color = colors[0]
      if (options.border && colors.map(c => c.toUpperCase()).includes('#FFFFFF')) {
        styles.borderWidth = '1px'
      }
    }

    if (bgImgs.length) {
      styles.backgroundImage = bgImgs.join(',')
      styles.backgroundSize = 'cover'
      styles.backgroundPosition = 'center'
    }

    return styles
  }

  /**
   * Parses a swatch color string into an object with name, colors, and style properties.
   * @param swatchColor - The swatch color string to parse, e.g. "Musta/valkoinen|#000000|#FFFFFF"
   * @returns An object containing the name, colors, and style properties.
   */
  const parseSwatchColor = (swatchColor: SwatchColor, options?: { border: boolean }): { name: string, colors: string[], style: CSSProperties } => {
    const swatchColorArray = swatchColor.split('|').filter(Boolean)
    const name = swatchColorArray[0] || ''
    const colors = swatchColorArray.slice(1)
    const style = getSwatchStyle(swatchColor, options)
    return { name, colors, style }
  }

  return {
    getSwatchStyle,
    parseSwatchColor
  }
}
